import { Show, type Component } from 'solid-js';

import type { GameStore } from '../state/store';
import { ACTION_ICONS, DODGE_KEY } from './actionbar';
import { Icon } from './Icon';

/**
 * The roll's readout: whether `SPACE` will actually roll, and what is stopping it.
 *
 * The dodge was the one action in the game with no affordance at all. It has a
 * three-second cooldown and a stamina cost, neither of which was drawn anywhere, so a
 * press during either read exactly like a broken key — the same failure the action bar's
 * refusal notice exists to fix, and the notice alone is not enough here: a message that
 * appears *after* a press cannot tell a player when to press.
 *
 * **A readout, not a button.** The roll lives on a key, and a second way to trigger it
 * would be two affordances for one action — the reasoning that keeps the basic-attack
 * slot a plain `div`. It carries no `.hud-interactive` of its own.
 *
 * **Four states, and a cooldown never reads as a shortage.** They are different problems
 * with different fixes — one is waited out, the other is a pool to stop spending — and
 * conflating them is the confusion this widget exists to remove. Each gets its own class,
 * its own `data-testid` and its own sentence.
 */
export const DodgeIndicator: Component<{ store: GameStore }> = (props) => {
  const state = () => props.store.dodgeState();

  /**
   * The last refusal, when it was a roll's.
   *
   * Read off the same `notice()` the action bar's message comes from rather than a signal
   * of its own: one press produces one refusal, and a second copy of that state could
   * flash for a press whose message had already aged out.
   */
  const refusal = () => {
    const n = props.store.notice();
    return n !== null && n.kind === 'dodge' ? n : null;
  };

  /**
   * What the roll's state is, in one word.
   *
   * The class, the testid and the sentence are all derived from it, so they cannot
   * disagree with each other. Order matters: a cooldown is reported even when stamina is
   * also short, because the cooldown is the wait the player can do nothing about.
   */
  const mode = (): 'dormant' | 'cooling' | 'tired' | 'ready' => {
    const s = state();
    if (!s.live) return 'dormant';
    if (s.cooldownRemaining > 0) return 'cooling';
    if (s.staminaShort) return 'tired';
    return 'ready';
  };

  /** Seconds left, as drawn. One decimal: a roll's whole cooldown is three seconds. */
  const remaining = () => state().cooldownRemaining.toFixed(1);

  /** The whole state as a sentence, for the tooltip and for a screen reader. */
  const sentence = () => {
    switch (mode()) {
      case 'dormant':
        return 'Dodge · dormant — no roll data yet';
      case 'cooling':
        return `Dodge in ${remaining()}s — ${DODGE_KEY}`;
      case 'tired':
        return `Dodge needs ${state().cost} stamina — ${DODGE_KEY}`;
      default:
        return `Dodge ready — ${DODGE_KEY}`;
    }
  };

  return (
    <div
      class="dodge"
      classList={{
        'dodge--dormant': mode() === 'dormant',
        'dodge--cooling': mode() === 'cooling',
        'dodge--tired': mode() === 'tired',
        'dodge--ready': mode() === 'ready',
      }}
      data-testid="dodge"
      role="progressbar"
      aria-label="Dodge"
      aria-valuemin={0}
      aria-valuemax={1}
      aria-valuenow={state().live ? 1 - state().cooldownFraction : 0}
      aria-valuetext={sentence()}
      title={sentence()}
    >
      {/* The glyph and the key are facts about the binding, not data about the
          character, so they are drawn in every state — including the dormant one,
          where there is nothing else honest to show. */}
      {/* Dormant keeps the em dash rather than the art: *you cannot do this yet* is the
          one state where drawing the roll would be a promise the character cannot keep,
          and it is the reason this is not simply an `<Icon>` in every mode. */}
      <Show when={mode() !== 'dormant'} fallback={<span class="dodge__sigil" aria-hidden="true">—</span>}>
        <Icon class="dodge__sigil" iconId={ACTION_ICONS.dodge} glyph="»" />
      </Show>
      <span class="dodge__key">{DODGE_KEY}</span>

      {/* Ready: a bright ring rather than a colour on the glyph, so the state reads
          at the size this tile actually is on screen. */}
      <Show when={mode() === 'ready'}>
        <span class="dodge__ring" data-testid="dodge-ready" aria-hidden="true" />
      </Show>

      {/* Cooling: `.cd-sweep` and `.cd-timer`, **the same two classes a skill slot wears**.
          The comment here used to claim they were already the same technique and they were
          not — two rules had drifted, and the skill slots ended up legible while this kept the
          old dark wash. One declaration now, so they cannot. */}
      <Show when={mode() === 'cooling'}>
        <span
          class="dodge__cooldown cd-sweep"
          data-testid="dodge-cooldown"
          style={{ '--sweep': String(state().cooldownFraction) }}
        />
        {/* The number as well as the wipe: a sweep says *soon*, and a player timing a
            roll against a monster's swing needs *how soon*. */}
        <span class="dodge__timer cd-timer" data-testid="dodge-timer">
          {remaining()}
        </span>
      </Show>

      {/* Short of stamina: the cost, in the stamina bar's own colour, so the fix is
          legible — this is a pool to stop spending, not a timer to wait out. */}
      <Show when={mode() === 'tired'}>
        <span class="dodge__cost" data-testid="dodge-stamina-short">
          {state().cost}
        </span>
      </Show>

      {/* Dormant before the first CHARACTER frame. Nothing is known then, so nothing
          is claimed — the rule the stamina bar follows, for the same reason. */}
      <Show when={mode() === 'dormant'}>
        <span class="dodge__note" data-testid="dodge-dormant">
          dormant
        </span>
      </Show>

      {/*
        The refusal flash.

        Keyed on the notice's `bornAt` so the element is rebuilt on every refusal —
        a CSS animation on a surviving element only ever plays once, which would
        make the second press of a mashed key look like nothing at all.
      */}
      <Show when={refusal()?.bornAt} keyed>
        <span class="dodge__flash" data-testid="dodge-flash" aria-hidden="true" />
      </Show>
    </div>
  );
};
